import { Component, OnDestroy, OnInit, Input, EventEmitter, Output, signal, WritableSignal } from '@angular/core';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { IdentifierService } from 'src/app/services/identifier.service';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { Integration } from 'src/app/models/integration';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { DynamicDropdownViewComponent } from '../dynamic-dropdown-view/dynamic-dropdown-view.component';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-identifier-dropdown-view',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    DynamicDropdownViewComponent,
    LTSpinnerComponent,
  ],
  templateUrl: './identifier-dropdown-view.component.html',
  styleUrl: './identifier-dropdown-view.component.scss',
})
export class IdentifierDropdownViewComponent implements OnInit, OnDestroy {
  destroyed$: Subject<boolean> = new Subject();
  @Input() infos: IntegrationInformation[] | null = null;
  @Input() destinations: DestinationEndpoint[] = [];
  @Input() destination: DestinationEndpoint = null;
  @Input() onlyDataSwap = false;
  @Input() onlyFastFetch = false;

  @Input() integration: Integration = null;
  @Input() noItemsMessage = 'No identifiers available.';

  itemFormControl = new UntypedFormControl();
  filteredOptions: WritableSignal<IntegrationInformation[]> = signal<IntegrationInformation[]>([]);

  selectedItem = signal<IntegrationInformation | null>(null);
  allInfos: IntegrationInformation[] | null = null;

  @Output() itemSelected = new EventEmitter<any>();
  @Output() loadingFailed = new EventEmitter<any>();
  @Input() labelName = 'Identifier';

  @Input() overrideItem: IntegrationInformation | null = null;
  @Output() itemSelectedFromOverride = new EventEmitter<any>();
  @Input() overrideIdentifierId: string = null;
  @Input() overrideIdentifierName: string = null;

  hasItems = false;

  constructor(
    private readonly idService: IdentifierService) { }

  ngOnInit(): void {
    if (this.destination != null && (this.destinations == null || this.destinations.length == 0))
    {
      this.destinations.push(this.destination);
    }
    this.getItems();
  }

  getItems(): void {
    this.hasItems = false;
    if (this.infos != null && this.infos.length > 0)
    {
      this.processItems(this.infos);
    }

    this.getDataCall()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        this.allInfos = data;

        if (this.infos == null || this.infos.length == 0)
        {
          if (data == null || data.length === 0)
          {
            this.loadingFailed.emit();
          }
          else {
            this.processItems(data);
          }
        }
      });
  }

  private processItems(items: IntegrationInformation[]) {
    if (items != null && items.length > 0)
    {
      if (this.destinations != null && this.destinations.length > 0)
      {
        const destIds = this.destinations.map(x => x.destinationEndpointId);
        items = items.filter(x => destIds.includes(x.destinationId));
      }

      if (this.overrideIdentifierId != null && this.overrideIdentifierName != null
            && this.overrideIdentifierId != undefined && this.overrideIdentifierName != undefined
      ){
        items = items.filter(x =>
              x.identifierIdValue == this.overrideIdentifierId && x.identifierNameValue == this.overrideIdentifierName,
        );
      }

      if (this.integration != null) {
        items = items.filter(x => x.integrationId == this.integration.integrationId);
      }

      this.infos = items;
      this.hasItems = true;

      if (items.length == 1 && this.overrideItem == null) {
          this.overrideItem = items[0];
      }
    }
  }

  public getDataCall(): Observable<IntegrationInformation[]>{
    if (this.onlyDataSwap)
    {
      return this.idService.getAllActiveIntegrationIdentifiersForDataSwap();
    }
    else if (this.onlyFastFetch)
    {
      return this.idService.getAllActiveIntegrationIdentifiersForFastFetch();
    }
    else {
      return this.idService.getAllActiveIntegrationInformation();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  editItem(): void {
    this.overrideItem = null;
    this.overrideIdentifierId = null;
    this.overrideIdentifierName = null;
    this.integration = null;
    if (this.allInfos != null && this.allInfos.length > 0)
    {
      this.processItems(this.allInfos);
    }
    this.selectedItem.set(null);
    this.itemFormControl.setValue('');
    this.itemSelected.emit(null);
  }

  selectItem(item: IntegrationInformation): void {
    this.selectedItem.set(item);
    this.itemSelected.emit(item);
  }

  selectItemForOverride(item: IntegrationInformation): void {
    this.selectedItem.set(item);
    this.itemSelectedFromOverride.emit(this.selectedItem());
  }

  public displayItem(item: IntegrationInformation): string {
    return `${item?.identifierIdValue ?? ''} - ${item?.identifierNameValue ?? ''}`;
  }

  public displayFnItem(x: IntegrationInformation): string {
    return x && x.identifierNameValue && x.identifierIdValue ? x.identifierIdValue + ' - ' + x.identifierNameValue : '';
  }

}
